import FormResult from 'components/Form/FormResult';
import InputHidden from 'components/Form/InputHidden';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import TextArea from 'components/Form/TextArea';
import Information from 'components/Information';
import Stacked from 'components/Layout/Stacked';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {c, jt, t} from 'ttag';
import {Backend} from 'types/api';
import {FormFields} from 'types/utils';
import getPreferredLanguage from 'utils/locale';
import {setUrl} from 'utils/request';
import {onApiError} from 'utils/request-axios/utils';

import {BillingCancelRequest, useBillingCancel} from './services';

export type ProfileCancelConfirmBackend = Backend<{
    partnerMid: string;
    usingVba?: boolean;
}>;

export type ProfileCancelConfirmFormData = FormFields<BillingCancelRequest>;

const CancelConfirm: FC = () => {
    const {data} = useBackend<ProfileCancelConfirmBackend>();
    const usingVba = data.usingVba;
    const methods = useForm<ProfileCancelConfirmFormData>();
    const {handleSubmit} = methods;

    const {mutateAsync} = useBillingCancel({partnerMid: data.partnerMid});

    const onSubmit = async (formData: ProfileCancelConfirmFormData) => {
        await mutateAsync(formData)
            .then(() => setUrl(window.Urls.billing_index()))
            .catch(onApiError(methods));
    };
    const informationLink = (
        <Link
            href="https://help.makeleaps.jp/ja/articles/5614653"
            isOpenInNewTab={true}
        >
            {c('informationLink').t`here`}
        </Link>
    );

    const pricingLink = (
        <Link href="https://www.makeleaps.jp/pricing/" isOpenInNewTab={true}>
            {c('pricingLink').t`here`}
        </Link>
    );
    const language = getPreferredLanguage();
    const handleContactUsClick = () => {
        if (language === 'ja') {
            window.location.href = 'https://www.makeleaps.jp/contact/';
        } else {
            window.location.href = 'https://www.makeleaps.com/en/plan-contact/';
        }
    };
    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stacked className="max-w-xl">
                    <Information size="sm" title={t`Warning`} type="alert">
                        <ul>
                            {usingVba && (
                                <li>
                                    {jt`You are currently using the Virtual Account feature. A written procedure is required to proceed. Please contact us for more details.`}
                                </li>
                            )}
                        </ul>

                        <ul>
                            <li>
                                {jt`By downgrading you will lose access to the Document Archive feature that you need for compliance with Japan’s Electronic Book Preservation Act. We strongly recommend you download and store your archive data before downgrading. (learn more about the Archives ${informationLink})`}
                            </li>
                        </ul>
                        <ul>
                            <li>
                                {jt`After downgrading, you can issue only Quote and Invoice document types. (learn more about the Free plan and its other restrictions ${pricingLink})`}
                            </li>
                        </ul>
                    </Information>
                    {!usingVba && (
                        <>
                            <div>{t`If you agree to these restrictions, please answer the survey below to proceed.`}</div>
                            <InputHidden name="cancel" value="yes" />
                            <Radio
                                label={t`Please select the main reason for the downgrade from the following.`}
                                name="selected_reason"
                                options={[
                                    {
                                        label: t`We don't create documents that much anymore`,
                                        value: 'no_need_for_doc_management',
                                    },
                                    {
                                        label: t`We decided to switch to a different product or system`,
                                        value: 'switched_to_competitor',
                                    },
                                    {
                                        label: t`MakeLeaps is missing important features`,
                                        value: 'lack_of_feature',
                                    },
                                    {
                                        label: t`We had a bad experience (bugs, designs, etc)`,
                                        value: 'bad_user_experience',
                                    },
                                    {
                                        label: t`It's too expensive for us`,
                                        value: 'expensive',
                                    },
                                    {
                                        label: t`Other`,
                                        value: 'other',
                                    },
                                ]}
                                required={true}
                            />
                            <TextArea
                                aria-label="reason_detail"
                                label={t`Please tell us more details or background of your cancellation.`}
                                name="reason_detail"
                                required={true}
                                rows={4}
                            />
                            <TextArea
                                aria-label="improvements"
                                label={t`Please let us know if there is any way we can improve our service.`}
                                name="improvements"
                                rows={4}
                            />
                        </>
                    )}

                    <FormResult />
                    <div className="flex justify-end">
                        <LinkButton className="mr-8" href="/" kind="tertiary">
                            {t`Cancel`}
                        </LinkButton>
                        {usingVba ? (
                            <div className="grid rounded bg-green-200">
                                <SubmitButton onClick={handleContactUsClick}>
                                    {t`Contact Us`}
                                </SubmitButton>
                            </div>
                        ) : (
                            <SubmitButton kind="destructive" type="submit">
                                {t`Confirm`}
                            </SubmitButton>
                        )}
                    </div>
                </Stacked>
            </form>
        </FormProvider>
    );
};

export default CancelConfirm;
